export const OFFER_FORMAT = 'pst-offer';
export const OFFER_FORMAT_VERSION = 1;
/** The offer as a file: ids replaced by keys of the parties and references it uses. */
export function offerToFile(offer, parties, references, now = new Date()) {
    const usedParties = new Set();
    for (const v of offer.variants) {
        v.data.partyIds.forEach((id) => usedParties.add(id));
        v.data.subcontractors.forEach((s) => s.partyId && usedParties.add(s.partyId));
    }
    offer.texts.attachments.cvPartyIds.forEach((id) => usedParties.add(id));
    const usedRefs = references.filter((r) => offer.texts.attachments.referenceIds.includes(r.id));
    usedRefs.forEach((r) => r.partyId && usedParties.add(r.partyId));
    const keep = parties.filter((p) => usedParties.has(p.id));
    const pKey = new Map(keep.map((p, i) => [p.id, `P${i + 1}`]));
    const rKey = new Map(usedRefs.map((r, i) => [r.id, `R${i + 1}`]));
    const party = (id) => (id ? pKey.get(id) ?? null : null);
    return {
        format: OFFER_FORMAT,
        version: OFFER_FORMAT_VERSION,
        exportedAt: now.toISOString(),
        offer: {
            code: offer.code, name: offer.name, client: offer.client, lot: offer.lot, location: offer.location, state: offer.state, status: offer.status,
            dueDate: offer.dueDate, offerDate: offer.offerDate, bindingDays: offer.bindingDays, description: offer.description,
            hoai: offer.hoai,
            feeBasis: offer.feeBasis ?? 'hours',
            texts: {
                ...offer.texts,
                attachments: {
                    ...offer.texts.attachments,
                    cvPartyIds: offer.texts.attachments.cvPartyIds.map(party).filter((k) => !!k),
                    referenceIds: offer.texts.attachments.referenceIds.map((id) => rKey.get(id)).filter((k) => !!k),
                },
            },
            variants: offer.variants.map((v) => ({
                code: v.code, name: v.name, constellation: v.constellation,
                data: {
                    ...v.data,
                    partyIds: v.data.partyIds.map(party).filter((k) => !!k),
                    subcontractors: v.data.subcontractors.map((s) => ({ ...s, partyId: party(s.partyId) })),
                },
            })),
        },
        parties: keep.map(({ id, iban: _i, accountHolder: _a, taxNo: _t, birthDate: _b, ...p }) => ({ ...p, key: pKey.get(id) })),
        references: usedRefs.map(({ id, partyId, ...r }) => ({ ...r, key: rKey.get(id), party: party(partyId) })),
    };
}
const norm = (s) => s.trim().toLocaleLowerCase('de');
/** Matches the file's parties and references to what this PST has: by name (parties) and title (references). */
export function planFile(file, parties, references) {
    const partyIds = new Map();
    const referenceIds = new Map();
    const createParties = [];
    const createReferences = [];
    const notes = [];
    for (const p of file.parties) {
        const found = parties.find((x) => norm(x.name) === norm(p.name));
        if (found)
            partyIds.set(p.key, found.id);
        else
            createParties.push(p);
    }
    for (const r of file.references) {
        const found = references.find((x) => norm(x.title) === norm(r.title));
        if (found)
            referenceIds.set(r.key, found.id);
        else
            createReferences.push(r);
    }
    if (createParties.length)
        notes.push(`${createParties.length} Beteiligte neu angelegt (${createParties.map((p) => p.name).join(', ')}); Bankverbindung, Steuernummer und Geburtsdatum bitte ergänzen.`);
    if (file.parties.length - createParties.length)
        notes.push(`${file.parties.length - createParties.length} Beteiligte über den Namen den vorhandenen zugeordnet.`);
    if (createReferences.length)
        notes.push(`${createReferences.length} Referenzen neu angelegt.`);
    return { partyIds, referenceIds, createParties, createReferences, notes };
}
/** The file's offer with keys replaced by ids (after the missing parties and references were created). */
export function resolveFile(file, partyIds, referenceIds) {
    const party = (k) => (k ? partyIds.get(k) ?? null : null);
    const o = file.offer;
    return {
        ...o,
        texts: {
            ...o.texts,
            attachments: {
                ...o.texts.attachments,
                cvPartyIds: o.texts.attachments.cvPartyIds.map(party).filter((id) => !!id),
                referenceIds: o.texts.attachments.referenceIds.map((k) => referenceIds.get(k)).filter((id) => !!id),
            },
        },
        variants: o.variants.map((v) => ({
            ...v,
            data: { ...v.data, partyIds: v.data.partyIds.map(party).filter((id) => !!id), subcontractors: v.data.subcontractors.map((s) => ({ ...s, partyId: party(s.partyId) })) },
        })),
    };
}
/** An empty profile, for parties from files that carry none. */
export const EMPTY_PARTY_PROFILE = { career: [], volunteer: [], education: [], qualifications: [], languages: '', software: '' };
//# sourceMappingURL=offer-exchange.js.map